import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Silent refresh of the JWT access token on 401 (once per request)
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url = cfg.url || "";
    if (error.response?.status === 401 && !cfg._retried && !url.startsWith("/auth/")) {
      cfg._retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return api(cfg);
      } catch {}
    }
    return Promise.reject(error);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (typeof d.msg === "string") return d.msg;
  return String(d);
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Poll a job-status URL every `ms` until ready/error. onTick receives each snapshot.
export async function pollJob(path, onTick, ms = 3000, maxMinutes = 20) {
  const deadline = Date.now() + maxMinutes * 60000;
  while (Date.now() < deadline) {
    const r = await api.get(path);
    onTick && onTick(r.data);
    if (r.data.status === "ready" || r.data.status === "error") return r.data;
    await sleep(ms);
  }
  throw new Error("Job polling timed out");
}

export const endpoints = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (body) => api.post("/auth/register", body),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),

  adminUsers: () => api.get("/admin/users"),
  adminInvite: (body) => api.post("/admin/users/invite", body),
  adminPatchUser: (id, body) => api.patch(`/admin/users/${id}`, body),
  adminApiKeys: () => api.get("/admin/api-keys"),
  adminSetApiKey: (provider, key) => api.put(`/admin/api-keys/${provider}`, { key }),

  adminLogin: (email, password) => api.post("/auth/admin-login", { email, password }),
  draftTeaser: (deal_id, investor_id) => api.post("/investor-match/teaser", { deal_id, investor_id }),
  sendOutreach: (body) => api.post("/outreach/send", body),
  digestSetting: () => api.get("/settings/digest"),
  setDigestSetting: (enabled) => api.post("/settings/digest", { enabled }),
  digestPreview: () => api.get("/digest/preview"),
  digestSendMe: () => api.post("/digest/send-me"),
  bulkDmStatus: (sid) => api.get(`/origin/bulk/${sid}/decision-makers`),
  bulkDmRun: (sid) => api.post(`/origin/bulk/${sid}/decision-makers`),
  cacheWarmupHistory: () => api.get("/admin/cache-warmup"),
  cacheWarmupRun: () => api.post("/admin/cache-warmup"),
  notifications: () => api.get("/notifications"),
  readNotification: (id) => api.post(`/notifications/${id}/read`),
  readAllNotifications: () => api.post("/notifications/read-all"),
  committeeWatch: () => api.get("/committee-watch"),
  committeeWatchAdd: (company_id) => api.post("/committee-watch", { company_id }),
  committeeWatchRemove: (id) => api.delete(`/committee-watch/${id}`),
  committeeWatchRerun: (watch_id) => api.post("/committee-watch/rerun", { watch_id }),
  dealInvestorMatches: (dealId, limit = 10) => api.get(`/deals/${dealId}/investor-matches?limit=${limit}`),
  investorDealMatches: (invId, limit = 10) => api.get(`/companies/${invId}/deal-matches?limit=${limit}`),
  explainMatch: (deal_id, investor_id) => api.post("/investor-match/explain", { deal_id, investor_id }),
  usageSummary: (scope = "me") => api.get(`/usage/summary?scope=${scope}`),
  usageEstimate: (op, units) => api.get(`/usage/estimate?op=${op}&units=${units}`),

  discoverMulti: (body) => api.post("/origin/discover-multi", body),
  intelLookup: (body) => api.post("/intel/lookup", body),
  intelProviders: () => api.get("/intel/providers"),
  entities: (q) => api.get(`/entities?q=${encodeURIComponent(q || "")}`),
  entity: (id) => api.get(`/entities/${id}`),
  companyEntity: (id) => api.get(`/companies/${id}/entity`),
  batchImport: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/batch-import", fd, { onUploadProgress });
  },
  batchExportUrl: (jobId, fmt) => `${API_BASE}/batch-import/${jobId}/export?format=${fmt}`,
  ddCreate: (companyId) => api.post(`/dd/company/${companyId}`),
  ddList: (companyId) => api.get(`/dd/company/${companyId}`),
  ddGet: (id) => api.get(`/dd/${id}`),
  ddPatch: (id, key, body) => api.patch(`/dd/${id}/items/${key}`, body),
  ddPdfUrl: (id) => `${API_BASE}/dd/${id}/pdf`,
  committeeRun: (company, session_id) => api.post("/committee/run", { company, session_id }),
  committeePdfUrl: (id) => `${API_BASE}/committee/${id}/pdf`,
  chatUpload: (session_id, file) => {
    const fd = new FormData();
    fd.append("session_id", session_id);
    fd.append("file", file);
    return api.post("/ai/chat/upload", fd);
  },

  me: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
  deleteAccount: () => api.delete("/auth/delete-account"),
  session: (session_id) => api.post("/auth/session", { session_id }),

  dashboard: () => api.get("/dashboard/summary"),

  companies: (params) => api.get("/companies", { params }),
  company: (id) => api.get(`/companies/${id}`),
  createCompany: (data) => api.post("/companies", data),
  deleteCompany: (id) => api.delete(`/companies/${id}`),

  deals: () => api.get("/deals"),
  createDeal: (data) => api.post("/deals", data),
  updateDeal: (id, data) => api.patch(`/deals/${id}`, data),
  deleteDeal: (id) => api.delete(`/deals/${id}`),

  valuations: () => api.get("/valuations"),
  getValuation: (id) => api.get(`/valuations/${id}`),
  valuationPdfUrl: (id) => `${API_BASE}/valuations/${id}/pdf`,
  generateValuation: (companyId) => api.post(`/ai/valuation/${companyId}`),
  originPerformance: () => api.get("/origin/performance"),
  originPerformancePdfUrl: () => `${API_BASE}/dashboard/performance/pdf`,

  risk: () => api.get("/risk"),
  analyzeRisk: (data) => api.post("/risk/analyze", data),

  legal: () => api.get("/legal"),
  analyzeLegal: (data) => api.post("/legal/analyze", data),
  deleteLegal: (id) => api.delete(`/legal/${id}`),

  projects: () => api.get("/projects"),
  createProject: (data) => api.post("/projects", data),
  updateProject: (id, data) => api.patch(`/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/projects/${id}`),

  scout: (filters) => api.post("/origin/scout", filters),
  discover: (body) => api.post("/origin/discover", body),
  discoverBulk: (body) => api.post("/origin/discover-bulk", body),
  promoteToDiligence: (body) => api.post("/origin/promote-to-diligence", body),
  bulkHistory: () => api.get("/origin/bulk/history"),
  bulkHistoryDetail: (id) => api.get(`/origin/bulk/history/${id}`),
  bulkCompare: (a, b) => api.get(`/origin/bulk/compare/${a}/${b}`),
  convergentWatchlist: () => api.get("/origin/convergent-watchlist"),
  registroImprese: (body) => api.post("/origin/registro-imprese", body),
  registroImpreseImport: (body) => api.post("/origin/registro-imprese/import", body),
  marketOverview: (symbol) => api.get(`/market/overview/${symbol}`),
  marketYahoo: (ticker) => api.get(`/market/yahoo/${encodeURIComponent(ticker)}`),
  enrichCompany: (id) => api.post(`/companies/${id}/enrich`),
  enrichDeep: (id, kind) => api.post(`/companies/${id}/enrich/${kind}`),
  markESG: (id, body) => api.post(`/companies/${id}/esg`, body),
  findDecisionMakers: (id) => api.post(`/companies/${id}/decision-makers`),
  outreachEmail: (id, dmIndex, body) => api.post(`/companies/${id}/decision-makers/${dmIndex}/outreach-email`, body || {}),
  originCategories: () => api.get("/origin/categories"),

  getEnrichmentSetting: () => api.get("/settings/enrichment"),
  setEnrichmentSetting: (enabled) => api.post("/settings/enrichment", { enabled }),

  chat: (session_id, message) => api.post("/ai/chat", { session_id, message }),
  chatHistory: (session_id) => api.get(`/ai/chat/${session_id}`),
};
